import {AfterContentInit, AfterViewChecked, AfterViewInit, Component, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {TruckFormService} from '../../services/truck-form/truck-form.service';
import {FormValidationService} from '../../services/form-validation/form-validation.service';
import {Router} from '@angular/router';
import TcoList from '../../models/tcoList';
import Tco from '../../models/tco';
import {Chart} from 'chart.js';
import {getCategoryColor, getDataValue, getTcoColorPallete, getTitleColor, PalleteIds} from '../../models/chartUtils';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {PdfService} from '../../services/pdf/pdf.service';
import {getFormTitle, getTcoKeyMapping} from '../../models/mappingUtils';
import {TruckModel, truckModels} from '../../models/truckModel';

type CalculatorSection = 'data' | 'assumptions' | 'results';
type ToggleFormKey = 'dieselForm' | 'electricForm' | 'hydrogenForm';


@Component({
  selector: 'app-truck-form',
  templateUrl: './calculator.component.html',
  styleUrls: ['./calculator.component.scss']
})
export class CalculatorComponent implements AfterViewChecked {

  private _currentSection: CalculatorSection = 'data';
  private firstLoad = true;

  dataSectionComplete = false;
  assumptionsSectionComplete = false;

  truckSelectionForm = this.formBuilder.group({
    dieselForm: new FormControl(true),
    electricForm: new FormControl(true),
    hydrogenForm: new FormControl(true)
  });

  toggleForm = {
    dieselForm: false,
    electricForm: false,
    hydrogenForm: false
  };

  dieselTruckForm = this._truckFormService.dieselForm ?? this.formBuilder.group({
    // Truck
    grossWeight: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    tare: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    vehicleChange: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    // Operation
    dailyDistance: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    performance: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    performanceSwitch: [false],
    averageSpeed: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    averageSpeedSwitch: [false],
    transport: ['regional', [Validators.required]],
    operatingDays: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    // Economy
    gasCost: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    maintenanceCost: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    maintenanceCostSwitch: [false],
    insuranceCost: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    insuranceCostSwitch: [false],
    truckPrice: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]]
  });

  electricTruckForm = this._truckFormService.electricForm ?? this.formBuilder.group({
    // Truck
    eGrossWeight: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    eTare: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    battery: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    batterySwitch: [false],
    // truckPower: [undefined, [Validators.required]],
    // modelSelectionToggle: [false],
    modelSelection: [undefined],
    // Operation
    uteFare: ['mc1', [Validators.required]],
    consumedPower: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    chargingSchedule: ['peak', [Validators.required]],
    chargingTime: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    // batteryReplacement: [true],
    // Economy
    eInsuranceCost: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    eInsuranceCostSwitch: [false],
    eTruckPrice: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    eTruckPriceSwitch: [false],
    comapDiscountSwitch: [false],
    // comapInvestment: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]]
  });

  hydrogenTruckForm = this._truckFormService.hydrogenForm ?? this.formBuilder.group({
    // Truck
    hGrossWeight: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    hTare: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    tankSize: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    tankSizeSwitch: [false],
    cellPower: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    cellPowerSwitch: [false],
    // Operation
    hydrogenCost: [undefined, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    hInsuranceCost: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    hInsuranceCostSwitch: [false],
    hTruckPrice: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    hTruckPriceSwitch: [false],
    // Additional Data
    hComapDiscountSwitch: [false],
    // comapInvestment: [undefined, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]]
  });

  assumptionsForm = this._truckFormService.assumptionsForm ?? this.formBuilder.group({
    inflation: [9, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    discountRate: [8, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    gasoil: [3.35, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    electricity: [7.23, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    hUpgradeRate: [1, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    dLifespan: [15, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    eLifespan: [15, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    batteryTimeWarranty: [8, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    batteryDistanceWarranty: [500000, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    batteryNumberOfCycles: [1500, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    chargingStationCostPerKW: [100, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    hLifespan: [12, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    hInfrastructureCost: [2.62, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    pesoDollarConv: [44, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  });

  resultsVariablesForm = this.formBuilder.group({
    dailyDistance: [30, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    operatingDays: [10, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    vehicleChange: [5, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    chargingSchedule: ['peak', [Validators.required]],
    pesoDollarConv: [44, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
    gasCost: [70, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]]
  });

  tcoList: TcoList = {
    dieselTCO: null,
    electricTCO: null,
    hydrogenTCO: null
  };

  dieselTcoDoughnutChart: Chart<'doughnut', any[], string> | null = null;
  electricTcoDoughnutChart: Chart<'doughnut', any[], string> | null = null;
  hydrogenTcoDoughnutChart: Chart<'doughnut', any[], string> | null = null;

  dieselTcoBarChart: Chart<'bar', any[], string> | null = null;
  electricTcoBarChart: Chart<'bar', any[], string> | null = null;
  hydrogenTcoBarChart: Chart<'bar', any[], string> | null = null;

  dieselAnnualCostsChart: Chart<'bar', any[], string> | null = null;
  electricAnnualCostsChart: Chart<'bar', any[], string> | null = null;
  hydrogenAnnualCostsChart: Chart<'bar', any[], string> | null = null;

  dieselCategoryChart: Chart<'bar', any[], string> | null = null;
  electricCategoryChart: Chart<'bar', any[], string> | null = null;
  hydrogenCategoryChart: Chart<'bar', any[], string> | null = null;

  // notAvailableList = {
  //   maintenanceCost: true,
  //   insuranceCost: true
  // };

  truckModels = [
    // new TruckModel('T4C', 4.3, 1.85, 63.63, 1.5),
    // new TruckModel('T45C01', 6.495, 2.8, 99, 1.2)
  ];

  printTruckForm(truckForm: FormGroup): void {
    console.log(truckForm.value);
  }

  constructor(private formBuilder: FormBuilder, private _truckFormService: TruckFormService, private pdfService: PdfService,
              private _formValidationService: FormValidationService, private router: Router) {
  }


  get truckFormService(): TruckFormService {
    return this._truckFormService;
  }

  ngAfterViewChecked(): void {
    if (this.currentSection === 'data') {
      this.setStyles();
    } else if (this.firstLoad && this.currentSection === 'results') {
      this.loadResultsForm();
      this.setBubbles();
      this.createCharts();
      this.firstLoad = false;
    }

    if (this.currentSection !== 'results') {
      this.firstLoad = true;
    }
  }

  handleFormToggle(formName: ToggleFormKey): void {
    this.toggleForm[formName] = !this.toggleForm[formName];
  }

  set currentSection(value: CalculatorSection) {
    this._currentSection = value;
  }

  get currentSection(): CalculatorSection {
    return this._currentSection;
  }

  get formValidationService(): FormValidationService {
    return this._formValidationService;
  }

  private createCharts(): void {
    this.createTcoDoughnutCharts();
    this.createTcoBarCharts();
    this.createAnnualCostsCharts();
    this.createCategoryCharts();
  }

  private createTcoDoughnutCharts(): void {
    const dieselTCO = this._truckFormService.dieselTCO;
    const electricTCO = this._truckFormService.electricTCO;
    const hydrogenTCO = this._truckFormService.hydrogenTCO;
    this.dieselTcoDoughnutChart = this.createTcoDoughnutChart(dieselTCO, 'diesel-tco-doughnut-chart', this.dieselTcoDoughnutChart);
    this.electricTcoDoughnutChart = this.createTcoDoughnutChart(electricTCO, 'electric-tco-doughnut-chart', this.electricTcoDoughnutChart);
    this.hydrogenTcoDoughnutChart = this.createTcoDoughnutChart(hydrogenTCO, 'hydrogen-tco-doughnut-chart', this.hydrogenTcoDoughnutChart);
  }

  private createTcoBarCharts(): void {
    this.dieselTcoBarChart = this.createTcoBarChart(this._truckFormService.dieselTCO, 'diesel-tco-bar-chart', this.dieselTcoBarChart);
    this.electricTcoBarChart = this.createTcoBarChart(this._truckFormService.electricTCO,
      'electric-tco-bar-chart', this.electricTcoBarChart);
    this.hydrogenTcoBarChart = this.createTcoBarChart(this._truckFormService.hydrogenTCO,
      'hydrogen-tco-bar-chart', this.hydrogenTcoBarChart);
  }

  private createAnnualCostsCharts(): void {
    this.dieselAnnualCostsChart = this.createAnnualCostsChart(this._truckFormService.dieselTCO, 'diesel-annual-chart',
      this.dieselAnnualCostsChart);
    this.electricAnnualCostsChart = this.createAnnualCostsChart(this._truckFormService.electricTCO, 'electric-annual-chart',
      this.electricAnnualCostsChart);
    this.hydrogenAnnualCostsChart = this.createAnnualCostsChart(this._truckFormService.hydrogenTCO, 'hydrogen-annual-chart',
      this.hydrogenAnnualCostsChart);
  }

  private createCategoryCharts(): void {
    this.dieselCategoryChart = this.createCategoryChart(this._truckFormService.dieselTCO, 'diesel-category-chart', this.dieselCategoryChart);
    this.electricCategoryChart = this.createCategoryChart(this._truckFormService.electricTCO,
      'electric-category-chart', this.electricCategoryChart);
    this.hydrogenCategoryChart = this.createCategoryChart(this._truckFormService.hydrogenTCO,
      'hydrogen-category-chart', this.hydrogenCategoryChart);
  }

  private createTcoDoughnutChart(tco: Tco | null, id: string,
                                 chart: Chart<'doughnut', any[], string> | null): Chart<any, any[], string> | null {
    if (chart) {
      chart.destroy();
    }

    const canvasContext = document.querySelector<HTMLCanvasElement>(`#${id}`)?.getContext('2d');

    if (!canvasContext) {
      return null;
    }

    if (tco) {
      const depreciation = tco.vehiclePrice - tco.residualValue - tco.batteryResidualValue - tco.discounts;
      const data = {
        depreciation: {
          label: 'Depreciación',
          value: depreciation,
          dieselColor: '#3B3838',
          electricColor: '#385623',
          hydrogenColor: '#073763'
        },
        fuel: {
          label: id.includes('electric') ? 'Energía' : 'Combustible',
          value: tco.fuel,
          dieselColor: '#767171',
          electricColor: '#4D7731',
          hydrogenColor: '#1F4E79'
        },
        maintenance: {
          label: 'Mantenimiento',
          value: tco.maintenance,
          dieselColor: '#AEAAAA',
          electricColor: '#63973F',
          hydrogenColor: '#25A0B6'
        },
        insurance: {label: 'Seguro', value: tco.insurance, dieselColor: '#BFBFBF', electricColor: '#A5D37B', hydrogenColor: '#2BBBD3'},
        patent: {label: 'Patente', value: tco.patent, dieselColor: '#D9D9D9', electricColor: '#A6C34E', hydrogenColor: '#7CD5E4'},
        infrastructure: {
          label: 'Infraestructura',
          value: tco.infrastructure,
          dieselColor: '#000',
          electricColor: '#BBD076',
          hydrogenColor: '#C8EEF4'
        }
      };

      const labels = Object.values(data).map(({label}) => label);
      const values = Object.values(data).map(({value}) => value);
      const colorSelection = id.includes('diesel') ? 'dieselColor' : id.includes('electric') ? 'electricColor' : 'hydrogenColor';
      const colors = Object.values(data).map(obj => obj[colorSelection]);

      const total = values.reduce((a, v) => v + a, 0);

      return new Chart(canvasContext, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            label: `${id} dataset`,
            data: values,
            backgroundColor: colors
          }]
        },
        plugins: [ChartDataLabels],
        options: {
          plugins: {
            legend: {
              display: false
            },
            datalabels: {
              display: true,
              align: 'end',
              anchor: 'end',
              formatter: (value, ctx) => {
                const dataLabels = ctx.chart.data.labels;
                const percentage = 100 * value / total;
                return (percentage < 2 || !dataLabels) ? '' : [dataLabels[ctx.dataIndex], `${+percentage.toFixed(1)} %`];
              },
              font: {
                weight: 'normal',
                size: 16,
                family: 'open-sans, sans-serif',
              },
              color: '#747171',
            }
          },
          layout: {
            padding: 50
          }
        }
      });
    }

    return null;
  }

  private createTcoBarChart(tco: Tco | null, id: string, chart: Chart<'bar', any[], string> | null): Chart<any, any[], string> | null {

    if (chart) {
      chart.destroy();
    }

    const canvasContext = document.querySelector<HTMLCanvasElement>(`#${id}`)?.getContext('2d');

    if (!canvasContext) {
      return null;
    }

    if (tco) {
      const palleteId = id.includes('diesel') ? PalleteIds.DIESEL : id.includes('electric') ? PalleteIds.ELECTRIC : PalleteIds.HYDROGEN;
      const pallete = getTcoColorPallete(palleteId);

      const tcoData = (data: number) => tco.discounts === 0 ? [data, 0, 0] : [data, 0, 0, 0];

      const tcoDiscountsVal = [0, tco.discounts].map(value => value + tco.getTCOWithDiscounts());
      const tcoDiscounts = tco.discounts === 0 ? [0, 0, 0] : [0, tcoDiscountsVal, 0, 0];

      const tcoResidualValueArr = [0, tco.getResidualValue()].map(value => value + tco.getTCO());
      const tcoResidualValue = tco.discounts === 0 ? [0, tcoResidualValueArr, 0] : [0, 0, tcoResidualValueArr, 0];

      const tcoTotal = tco.discounts === 0 ? [0, 0, tco.getTCO()] : [0, 0, 0, tco.getTCO()];

      const labels = tco.discounts === 0 ? ['Costos', 'Valores Residuales', 'Costo total de propiedad'] :
        ['Costos', 'Descuento COMAP', 'Valores Residuales', 'Costo total de propiedad'];

      return new Chart(canvasContext, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Precio Camión',
            data: tcoData(tco.vehiclePrice),
            stack: 'tco',
            backgroundColor: pallete.vehiclePrice
          }, {
            label: id.includes('electric') ? 'Energía' : 'Combustible',
            data: tcoData(tco.fuel),
            stack: 'tco',
            backgroundColor: pallete.fuel
          }, {
            label: 'Mantenimiento',
            data: tcoData(tco.maintenance),
            stack: 'tco',
            backgroundColor: pallete.maintenance
          }, {
            label: 'Seguro',
            data: tcoData(tco.insurance),
            stack: 'tco',
            backgroundColor: pallete.insurance
          }, {
            label: 'Patente',
            data: tcoData(tco.patent),
            stack: 'tco',
            backgroundColor: pallete.patent
          }, {
            label: 'Infraestructura',
            data: tcoData(tco.infrastructure),
            stack: 'tco',
            backgroundColor: pallete.infrastructure
          }, {
            label: 'Descuento COMAP',
            data: tcoDiscounts,
            stack: 'tco',
            backgroundColor: pallete.discounts,
          }, {
            label: 'Valores Residuales',
            data: tcoResidualValue,
            stack: 'tco',
            backgroundColor: pallete.discounts,
          }, {
            label: 'Costo total de propiedad',
            data: tcoTotal,
            stack: 'tco',
            backgroundColor: pallete.tco
          }]
        },
        plugins: [ChartDataLabels],
        options: {
          layout: {
            padding: {
              top: 50,
              bottom: 50,
              left: 50,
              right: 50
            }
          },
          plugins: {
            datalabels: {
              anchor: 'end',
              align: 'end',
              formatter: (value, ctx) => {
                const datasets = ctx.chart.data.datasets;
                return ctx.datasetIndex !== datasets.length - 1 ? '' :
                  [Math.round(datasets.reduce((accum, dataset) => accum + getDataValue(dataset, ctx.dataIndex), 0)), 'USD'];
              },
              font: {
                weight: 'normal',
                size: 24,
                family: 'open-sans, sans-serif',
              },
              color: '#000',
              textAlign: 'center'
            },
            tooltip: {
              filter: ({raw}) => {
                return raw !== 0;
              },
              callbacks: {
                label: tooltipItem => {
                  const value = Array.isArray(tooltipItem.raw) ?
                    tooltipItem.raw.length !== 2 ? -1 : tooltipItem.raw[1] - tooltipItem.raw[0] : tooltipItem.raw;
                  return `${tooltipItem.dataset.label}: ${value}`;
                }
              }
            },
            legend: {
              display: false
            }
          },
          elements: {
            bar: {
              borderRadius: 10
            }
          },
          datasets: {
            bar: {
              categoryPercentage: 1,
              barPercentage: .9
            }
          },
          scales: {
            x: {
              ticks: {
                font: {
                  family: 'open-sans, sans-serif',
                  size: 16,
                  weight: 'normal'
                },
                color: '#747171'
              }
            },
            y: {
              ticks: {
                font: {
                  family: 'open-sans, sans-serif',
                  size: 16,
                  weight: 'normal'
                },
                color: '#747171'
              }
            }
          }
        }
      });
    }

    return null;
  }

  private createAnnualCostsChart(tco: Tco | null, id: string,
                                 chart: Chart<'bar', any[], string> | null): Chart<'bar', any[], string> | null {
    if (chart) {
      chart.destroy();
    }

    const canvasContext = document.querySelector<HTMLCanvasElement>(`#${id}`)?.getContext('2d');

    if (!tco || !canvasContext) {
      return null;
    }

    const numberOfYears = tco.annualFuelCosts.length;

    const averageDepreciation = (tco.vehiclePrice - tco.getDiscountsAndResidualValue()) / numberOfYears;
    const annualDepreciationCosts = Array(numberOfYears).fill(averageDepreciation);

    const averageInfrastructureCost = tco.infrastructure / numberOfYears;
    const annualInfrastructureCosts = id.includes('electric') ? Array(numberOfYears).fill(0).fill(tco.infrastructure, 0, 1)
      : Array(numberOfYears).fill(averageInfrastructureCost);

    const labels = [...Array(numberOfYears).keys()].map((_, i) => `Año ${i + 1}`);

    const [palleteId, title] = id.includes('diesel') ? [PalleteIds.DIESEL, 'Camión diésel']
      : id.includes('electric') ? [PalleteIds.ELECTRIC, 'Camión eléctrico'] : [PalleteIds.HYDROGEN, 'Camión hidrógeno'];
    const pallete = getTcoColorPallete(palleteId);
    const titleColor = getTitleColor(palleteId);

    return new Chart(canvasContext, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: 'Depreciación',
          data: annualDepreciationCosts,
          backgroundColor: pallete.vehiclePrice
        }, {
          label: id.includes('electric') ? 'Energía' : 'Combustible',
          data: tco.annualFuelCosts,
          backgroundColor: pallete.fuel
        }, {
          label: 'Mantenimiento',
          data: tco.annualMaintenanceCosts,
          backgroundColor: pallete.maintenance
        }, {
          label: 'Seguro',
          data: tco.annualInsuranceCosts,
          backgroundColor: pallete.insurance
        }, {
          label: 'Patente',
          data: tco.annualPatentCosts,
          backgroundColor: pallete.patent
        }, {
          label: 'Infraestructura',
          data: annualInfrastructureCosts,
          backgroundColor: pallete.infrastructure
        }]
      },
      options: {
        plugins: {
          legend: {
            position: 'right',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              filter: (item, data) => {
                return data.datasets[item.datasetIndex].data.some(value => value !== 0);
              },
              font: {
                family: 'open-sans, sans-serif',
                size: 20,
              },
              padding: 50
            },
          },
          title: {
            align: 'start',
            text: title,
            font: {
              family: 'Kollektif, sans-serif',
              size: 40
            },
            color: titleColor,
            padding: {
              bottom: 30
            }
          }
        },
        scales: {
          x: {
            stacked: true,
            grid: {
              display: false
            },
            ticks: {
              font: {
                family: 'open-sans, sans-serif',
                size: 16,
                weight: 'normal'
              },
              color: '#747171'
            },
            title: {
              display: true,
              color: '#747171',
              font: {
                family: 'open-sans, sans-serif',
                weight: 'normal',
                size: 32
              },
              text: 'Años'
            }
          },
          y: {
            stacked: true,
            ticks: {
              font: {
                family: 'open-sans, sans-serif',
                size: 16,
                weight: 'normal'
              },
              color: '#747171'
            },
            title: {
              display: true,
              color: '#747171',
              font: {
                family: 'open-sans, sans-serif',
                weight: 'normal',
                size: 32
              },
              text: 'USD',
            }
          }
        },
        elements: {
          bar: {
            borderRadius: 10
          }
        },
        datasets: {
          bar: {
            categoryPercentage: 1,
            barPercentage: .9
          }
        }
      }
    });
  }

  private createCategoryChart(tco: Tco | null, id: string, chart: Chart<'bar', any[], string> | null): Chart<'bar', any[], string> | null {
    if (chart) {
      chart.destroy();
    }

    const canvasContext = document.querySelector<HTMLCanvasElement>(`#${id}`)?.getContext('2d');

    if (!tco || !canvasContext) {
      return null;
    }
    const depreciation = tco.vehiclePrice - tco.residualValue - tco.batteryResidualValue - tco.discounts;

    const data = {
      depreciation: {
        label: 'Depreciación',
        value: depreciation
      },
      fuel: {
        label: id.includes('electric') ? 'Energía' : 'Combustible',
        value: tco.fuel
      },
      maintenance: {
        label: 'Mantenimiento',
        value: tco.maintenance
      },
      insurance: {
        label: 'Seguro',
        value: tco.insurance
      },
      patent: {
        label: 'Patente',
        value: tco.patent
      },
      infrastructure: {
        label: 'Infraestructura',
        value: tco.infrastructure
      }
    };

    const labels = Object.values(data).filter(({value}) => value !== 0).map(({label}) => label);
    const values = Object.values(data).filter(({value}) => value !== 0).map(({value}) => value);
    const palleteId = id.includes('diesel') ? PalleteIds.DIESEL : id.includes('electric') ? PalleteIds.ELECTRIC : PalleteIds.HYDROGEN;
    const backgroundColor = getCategoryColor(palleteId);
    const title = 'Camión ' + (id.includes('diesel') ? 'diésel' : id.includes('electric') ? 'eléctrico' : 'a hidrógeno');
    const titleColor = getTitleColor(palleteId);

    return new Chart(canvasContext, {
      type: 'bar',
      data: {
        labels,
        datasets: [{
          label: `${id} dataset`,
          data: values,
          backgroundColor
        }]
      },
      options: {
        plugins: {
          legend: {
            display: false
          },
          title: {
            display: true,
            text: title,
            color: titleColor,
            font: {
              family: 'Kollektif, sans-serif',
              size: 40,
              weight: '900'
            },
            padding: {
              bottom: 30
            }
          }
        },
        elements: {
          bar: {
            borderRadius: 10
          }
        },
        datasets: {
          bar: {
            categoryPercentage: 1,
            barPercentage: .9
          }
        },
        scales: {
          x: {
            ticks: {
              font: {
                family: 'open-sans, sans-serif',
                size: 16,
                weight: 'normal'
              },
              color: '#747171'
            }
          },
          y: {
            ticks: {
              font: {
                family: 'open-sans, sans-serif',
                size: 16,
                weight: 'normal'
              },
              color: '#747171'
            },
            title: {
              display: true,
              color: '#747171',
              font: {
                family: 'open-sans, sans-serif',
                weight: 'normal',
                size: 32
              },
              text: 'USD'
            }
          }
        }
      }
    });
  }

  private setStyles(): void {
    document.querySelectorAll<HTMLElement>('.suffix').forEach(span => {
      const rightPadding = span.clientWidth;
      const inputSibling = span.previousElementSibling as HTMLElement;
      const inputSiblingWidth = inputSibling.clientWidth;
      inputSibling.style.paddingRight = `calc(${rightPadding}px + 1.1em)`;
      // inputSibling.style.maxWidth = `100%`;
    });

    // console.log(document.querySelector<HTMLElement>('#gross-weight')?.parentElement);
    // const inputMaxWidth = document.querySelector<HTMLElement>('#gross-weight')?.parentElement?.style.maxWidth;
    // console.log(`inputMaxWidth: ${inputMaxWidth}`);
    // if (!inputMaxWidth) {
    //   return;
    // }

    // document.querySelectorAll<HTMLElement>('.input-container').forEach(div => {
    //   div.style.maxWidth = inputMaxWidth;
    // });


    // document.querySelectorAll<HTMLElement>('.select').forEach(selector => {
    //   selector.style.maxWidth = inputMaxWidth;
    // });
  }

  private setTCO(key: keyof TcoList, val: Tco): void {
    this.tcoList[key] = val;
  }

  onSubmit(section: CalculatorSection, btn: HTMLElement): void {

    if (!this.atLeastOneFormSelected()) {
      this.showNoFormSelectedMsg(btn);
      return;
    }

    // Validate Forms
    {
      if (!this.areFormsValid()) {
        this.showInvalidFormMsg(btn);
        return;
      }
    }

    // Save Forms in service
    if (this.truckSelectionForm.get('dieselForm')?.value) {
      this._truckFormService.saveDieselForm(this.dieselTruckForm);
    }

    if (this.truckSelectionForm.get('electricForm')?.value) {
      this._truckFormService.saveElectricForm(this.electricTruckForm);
    }

    if (this.truckSelectionForm.get('hydrogenForm')?.value) {
      this._truckFormService.saveHydrogenForm(this.hydrogenTruckForm);
    }

    this.dataSectionComplete = true;

    this._truckFormService.setFormBooleans(
      this.truckSelectionForm.get('dieselForm')?.value,
      this.truckSelectionForm.get('electricForm')?.value,
      this.truckSelectionForm.get('hydrogenForm')?.value);

    if (section === 'results') {
      this._truckFormService.calculateTCO();
    }

    this.currentSection = section;
  }

  private showNoFormSelectedMsg(btn: HTMLElement): void {
    this.startAnimation(btn, 'show-no-form-selected-error');
  }

  private showInvalidFormMsg(btn: HTMLElement): void {
    this.startAnimation(btn, 'show-invalid-form-error');
  }

  private showAssumptionsIncompleteMsg(btn: HTMLElement): void {
    this.startAnimation(btn, 'show-assumptions-incomplete-error');
  }

  private startAnimation(btn: HTMLElement, className: string): void {
    ['show-no-form-selected-error', 'show-invalid-form-error', 'show-assumptions-incomplete-error'].forEach(name => {
      btn.classList.remove(name); });
    // tslint:disable-next-line:no-unused-expression
    void (btn.offsetWidth);
    btn.classList.add(className);
  }

  private atLeastOneFormSelected(): boolean {
    return Object.keys(this.truckSelectionForm.controls).reduce(
      (sum, control) => sum + this.truckSelectionForm.get(control)?.value ? 1 : 0, 0) >= 1;
  }

  private areFormsValid(): boolean {
    let formsValidated = true;

    if (this.truckSelectionForm.get('dieselForm')?.value) {
      formsValidated = formsValidated && this.isFormValid(this.dieselTruckForm);
    }

    if (this.truckSelectionForm.get('electricForm')?.value) {
      formsValidated = formsValidated && this.isFormValid(this.electricTruckForm);
    }

    if (this.truckSelectionForm.get('hydrogenForm')?.value) {
      formsValidated = formsValidated && this.isFormValid(this.hydrogenTruckForm);
    }

    return formsValidated;
  }

  private isFormValid(truckForm: FormGroup): boolean {
    // return Object.keys(truckForm.controls).every(control => truckForm.get(control)?.valid);
    return truckForm.valid;
  }

  assumptionsOnSubmit(section: CalculatorSection, btn: HTMLElement): void {
    if (!this.isFormValid(this.assumptionsForm)) {
      this.showInvalidFormMsg(btn);
      return;
    }

    this._truckFormService.saveAssumptionsForm(this.assumptionsForm);
    if (section === 'results') {
      this.assumptionsSectionComplete = true;
      this._truckFormService.calculateTCO();
    }
    this.currentSection = section;
  }

  goToSection(section: CalculatorSection, target: EventTarget | null): void {

    const btn = target as HTMLElement;

    if (!btn) {
      return;
    }

    if (section === this._currentSection ||
      (section === 'assumptions' && !this.dataSectionComplete && this.currentSection !== 'data')) {
      return;
    }

    if (section === 'results' && !this.assumptionsSectionComplete && this.currentSection !== 'assumptions') {
      this.showAssumptionsIncompleteMsg(btn);
      return;
    }

    if (this._currentSection === 'data') {
      this.onSubmit(section, btn);
    } else if (this._currentSection === 'assumptions') {
      this.assumptionsOnSubmit(section, btn);
    } else {
      this.currentSection = section;
    }

  }

  async downloadReport(): Promise<void> {
    const selectedTrucks = ['diesel', 'electric', 'hydrogen'].filter(val => this.truckSelectionForm.value[`${val}Form`]);

    const [tcoDoughnutCharts, tcoBarCharts, annualCharts, categoryCharts] = ['tco-doughnut', 'tco-bar', 'annual', 'category']
      .map(chartType => {
        return selectedTrucks.map(truck => {
          return document.querySelector<HTMLCanvasElement>(`#${truck}-${chartType}-chart`);
        }).filter(truck => truck) as HTMLCanvasElement[];
      });
    // const tcoDoughnutCharts = selectedTrucks.map(value => document.querySelector<HTMLCanvasElement>(`#${value}-tco-doughnut-chart`));
    // const tcoBarCharts = selectedTrucks.map(value => document.querySelector<HTMLCanvasElement>(`#${value}-tco-bar-chart`));
    // const annualCharts = selectedTrucks.map(value => document.querySelector<HTMLCanvasElement>(`#${value}-annual-chart`));
    // const categoryCharts = selectedTrucks.m

    if (!tcoDoughnutCharts || !tcoBarCharts || !annualCharts || !categoryCharts || !this.truckFormService.dieselTCO) {
      return;
    }


    const charts: HTMLCanvasElement[] = Array.from(document.querySelectorAll<HTMLCanvasElement>('canvas'));
    const pdf = await this.pdfService.generatePdf(Object.entries(this._truckFormService.formsWrapper), tcoDoughnutCharts,
      tcoBarCharts, annualCharts, categoryCharts,
      {
        dieselTCO: {title: 'Camión Diésel', value: this.truckFormService.dieselTCO},
        electricTCO: this.truckFormService.electricTCO ? {title: 'Camión Eléctrico', value: this.truckFormService.electricTCO} : undefined,
        hydrogenTCO: this.truckFormService.hydrogenTCO ? {title: 'Camión a Hidrógeno', value: this.truckFormService.hydrogenTCO} : undefined
      });
    // this.pdfService.dowloadPdf(pdf);
    this.pdfService.dowloadPdf(pdf);
  }

  handleFormTitleMapping(formName: string): string {
    return getFormTitle(formName);
  }

  handleTcoKeyMapping(key: string): string {
    return getTcoKeyMapping(key).label;
  }

  originalOrder = (a: any, b: any): number => 0;

  // toggleSwitch(field: string): void {
  //
  //   // if (!this.isObjKey(field, this.notAvailableList)) {
  //   //   return;
  //   // }
  //
  //   // this.notAvailableList[field] = !this.notAvailableList[field];
  //   this.dieselTruckForm.get(field)?.setValue(!this.dieselTruckForm.get(field)?.value);
  //   console.log(this.dieselTruckForm);
  //   // this.notAvailableList = {...this.notAvailableList, field : !this.notAvailableList[field]};
  // }

  isObjKey<T>(key: any, obj: T): key is keyof T {
    return key in obj;
  }

  isNumber(val: any): boolean {
    return typeof val === 'number';
  }

  getTcoVal(tco: Tco, key: string, mappingFunc: (val: number) => number): number {
    return this.isObjKey(key, tco) && typeof tco[key] === 'number' ? mappingFunc(tco[key] as number) : 0;
  }

  setTruckModel(): void {
    const model = truckModels.find(
      ({model, brand}) => this.electricTruckForm.get('modelSelection')?.value === `${brand}-${model}`);

    if (!model) {
      return;
    }

    const electricTruckFields = ['eGrossWeight', 'eTare', 'battery', 'eTruckPrice', 'chargingTime'];
    const electricSwitches = [['batterySwitch', 'battery'], ['eTruckPriceSwitch', 'eTruckPrice']];
    const assumptionsFormFields = ['batteryTimeWarranty', 'batteryDistanceWarranty'];

    // Object.entries(newValue).filter(([key]) => electricTruckFields.includes(key)).forEach(([key, value]) => {
    //   if (!value) {
    //     this.electricTruckForm.get(key)?.setValue('');
    //     return;
    //   }
    //
    //   this.electricTruckForm.get(key)?.setValue(value);
    // });
    this.setFormValues(this.electricTruckForm, electricTruckFields, model);

    electricSwitches.forEach(([switchVal, modelField]) => {
      this.electricTruckForm.get(switchVal)?.setValue(!model[modelField as keyof TruckModel]);
    });

    this.setFormValues(this.assumptionsForm, assumptionsFormFields, model, false);

    // console.log(this.electricTruckForm.get('modelSelection')?.value);
    // console.log(truckModels.map(({brand, model}) => `${brand}-${model}`));
    // this.electricTruckForm.get('modelSelection')?.setValue('');
  }

  setFormValues(formGroup: FormGroup, formFields: string[], model: TruckModel, unsetEmptyFields: boolean = true): void {
    Object.entries(model).filter(([key]) => formFields.includes(key)).forEach(([key, value]) => {
      if (!value) {
        if (unsetEmptyFields) {
          formGroup.get(key)?.setValue('');
        }
        return;
      }

      formGroup.get(key)?.setValue(value);
    });
  }

  getTruckModels(): TruckModel[] {
    return truckModels;
  }

  setBubbles(): void {
    document.querySelectorAll('.slider').forEach(input => this.setBubble(input));
  }

  setBubble(target: EventTarget | null): void {

    if (!target) {
      return;
    }

    const range = target as HTMLInputElement;
    const bubble = range.parentElement?.querySelector('.output-bubble') as HTMLOutputElement;

    if (!bubble) {
      return;
    }

    const val = parseFloat(range.value);
    const min = range.min ? parseFloat(range.min) : 0;
    const max = range.max ? parseFloat(range.max) : 100;
    const newVal = Number((val - min) * 100 / (max - min));
    bubble.innerHTML = val.toString();
    bubble.style.left = `calc(${newVal}% + (${8 - newVal * 0.15}px))`;
  }

  onResultsFormChange(): void {
    if (this.resultsVariablesForm.invalid) {
      return;
    }

    this.setForms();
    this.saveForms();
    this._truckFormService.calculateTCO();
    this.createCharts();
  }

  setForms(): void {
    this.dieselTruckForm.get('dailyDistance')?.setValue(this.resultsVariablesForm.get('dailyDistance')?.value);
    this.dieselTruckForm.get('operatingDays')?.setValue(this.resultsVariablesForm.get('operatingDays')?.value);
    this.dieselTruckForm.get('vehicleChange')?.setValue(this.resultsVariablesForm.get('vehicleChange')?.value);
    this.electricTruckForm.get('chargingSchedule')?.setValue(this.resultsVariablesForm.get('chargingSchedule')?.value);
    this.assumptionsForm.get('pesoDollarConv')?.setValue(this.resultsVariablesForm.get('pesoDollarConv')?.value);
    this.dieselTruckForm.get('gasCost')?.setValue(this.resultsVariablesForm.get('gasCost')?.value);
  }

  saveForms(): void {
    this._truckFormService.saveDieselForm(this.dieselTruckForm);
    this._truckFormService.saveElectricForm(this.electricTruckForm);
    this._truckFormService.saveAssumptionsForm(this.assumptionsForm);
  }

  private loadResultsForm(): void {
    this.resultsVariablesForm.get('dailyDistance')?.setValue(this.dieselTruckForm.get('dailyDistance')?.value);
    this.resultsVariablesForm.get('operatingDays')?.setValue(this.dieselTruckForm.get('operatingDays')?.value);
    this.resultsVariablesForm.get('vehicleChange')?.setValue(this.dieselTruckForm.get('vehicleChange')?.value);
    this.resultsVariablesForm.get('chargingSchedule')?.setValue(this.electricTruckForm.get('chargingSchedule')?.value);
    this.resultsVariablesForm.get('pesoDollarConv')?.setValue(this.assumptionsForm.get('pesoDollarConv')?.value);
    this.resultsVariablesForm.get('gasCost')?.setValue(this.dieselTruckForm.get('gasCost')?.value);
  }

  // private loadTruckFormGroup(): FormGroup {
  //   return this.truckFormService.truckFormGroup === null ?
  //     // FormGroup with TCO form fields and their corresponding validators
  //     this.formBuilder.group({
  //       // Truck
  //       grossWeight: [10, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       tare: [4, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       vehicleChange: [10, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       // Trajectory
  //       distanceTraveled: [150, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       performance: [4.5, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       averageSpeed: [30, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       transport: ['regional', [Validators.required]],
  //       operatingDays: [350, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       // Economy
  //       gasCost: [1.2, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       maintenanceCost: [5000, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       insuranceCost: [1500, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       truckPrice: [90000, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       // Truck
  //       eGrossWeight: [15, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       eTare: [5, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       battery: ['', [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       // Electricity
  //       uteFare: ['mc1', [Validators.required]],
  //       hiredPower: [800, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       chargingSchedule: ['peak', [Validators.required]],
  //       chargingTime: [8, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       // Economy
  //       eInsuranceCost: [1650, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       eTruckPrice: [123000, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       comapDiscount: [false],
  //       // eLifespan: ['', [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       // Truck
  //       hGrossWeight: [12, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       hTare: [3, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       tankSize: ['', [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       cellPower: ['', [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       // Economy
  //       hydrogenCost: [9, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       hInsuranceCost: [1650, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       hTruckPrice: [170000, [Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       // hLifespan: ['', [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       // Additional Data
  //       pesoDollarConv: [44, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]]
  //       // inflation: [3, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)')]],
  //       // discountRate: [2, [Validators.required, Validators.pattern('([0-9]+([.][0-9]*)?|[.][0-9]+)'), Validators.max(100)]]
  //     }) : this.truckFormService.truckFormGroup;
  // }

  // Is called when the html tag finds a unsatisfied validator condition
  // Returns corresponding error message
  // getErrorMessage(fieldName: string): string {
  //
  //   if (this.truckForm.get(fieldName)?.hasError('required')) {
  //     return 'Campo obligatorio';
  //   } else if (this.truckForm.get(fieldName)?.hasError('pattern') || this.truckForm.get(fieldName)?.hasError('max')) {
  //     return 'Formato inválido';
  //   }
  //
  //   return 'Unexpected call';
  // }
  // isChargingScheduleValid(): boolean {
  //   return !['rs', 'gs'].includes(this.truckForm.get('uteFare')?.value);
  // }

  // On submit
  // submitForm(): void {
  //
  //   if (!this.formValidationService.formIsValid(this.truckForm)) {
  //     return;
  //   }
  //
  //   this.truckFormService.saveTruckFormGroup(this.truckForm);
  //
  //   this.router.navigateByUrl('/supuestos');
  //   // if (this._formValidationService.formIsValid(this.truckForm)) {
  //   //   this.truckFormService.calculateTCO(this.truckForm);
  //   //   console.log('form sent');
  //   // } else {
  //   //   console.log('invalid form');
  //   // }
  // }

  // Verify field validators in each field
  // formIsValid(): boolean {
  //   for (const key of Object.keys(this.truckForm.controls)) {
  //     if (this.truckForm.get(key)?.errors != null) {
  //       return false;
  //     }
  //   }
  //
  //   return true;
  // }

  // modifies chargingSchedule value if the current one is invalid
  // verifychargingSchedule(): void {
  //   if (!this.isChargingScheduleValid()) {
  //     this.truckForm.get('charchingSchedule')?.setValue('valley');
  //   }
  // }

  // checks if the tare is less or equal than the grossWeight
  // verifyTare(type: string): void {
  //   let grossWeightField: string;
  //   let tareField: string;
  //
  //   // define truck type
  //   if (type === 'diesel') {
  //     grossWeightField = 'grossWeight';
  //     tareField = 'tare';
  //   } else if (type === 'electric') {
  //     grossWeightField = 'eGrossWeight';
  //     tareField = 'eTare';
  //   } else {
  //     grossWeightField = 'hGrossWeight';
  //     tareField = 'hTare';
  //   }
  //
  //   if (!this.formValidationService.fieldIsValid(this.truckForm, grossWeightField) ||
  //     !this.formValidationService.fieldIsValid(this.truckForm, tareField)) {
  //     return;
  //   }
  //
  //   const grossWeight = TruckFormService.getNumberValue(this.truckForm, grossWeightField);
  //   const tare = TruckFormService.getNumberValue(this.truckForm, tareField);
  //
  //   if (tare > grossWeight) {
  //     this.truckForm.get(tareField)?.setValue(this.truckForm.get(grossWeightField)?.value);
  //   }
  // }

}

